import appEnv from 'app-env';
import { createAnalyticsManager } from '@helu/core';
import type { BreadcrumbItem, ColorThemeType } from '@helu/hds';
import { authManager } from '@/core/auth';
import type { BookingsContext } from '@/features/centralized-data/centralizedData.type';
import type { NegativeNumberFormat } from '@/features/widgets/definitions/widget.definition.ts';
import { ScenarioDateRangeType } from '@/features/widgets/widgets.type';
import type { GqlUpsertIntegrationDimensionMemberAttributesInput } from '@/gql/generated-composables';
import { isPreviewEnvironment, isProductionEnvironment } from './env/env.util';

export type UserEvents = {
  //#region Report Events
  REPORT_CREATED: void;
  REPORT_VIEWED: { widgetCount: number; widgetTypes: string[] };
  REPORT_DELETED: void;
  REPORT_DYNAMIC_DATE_RANGE_USED: {
    reportId: UUID;
    rangeType: ScenarioDateRangeType;
  };
  REPORT_SCENARIO_DATE_RANGES_USED: {
    reportId: UUID;
    scenarioDateRangesCount: number;
  };
  //#endregion

  //#region Data platform
  BULK_SAVE_NEW: GqlUpsertIntegrationDimensionMemberAttributesInput[];
  //#endregion

  //#region Accounts Table
  ACCOUNT_NATURE_CHANGED: {
    accountCode: string;
    oldNature: string;
    newNature: string;
  };
  ACCOUNT_LABEL_CHANGED: { accountCode: string };
  //#endregion

  //#region Cost Center Table
  COST_CENTER_LABEL_CHANGED: { costCenterCode: string };
  //#endregion

  //#region Widget Events
  WIDGET_ADDED: { widgetType: string; chartType: string };
  WIDGET_MOVED: void;
  WIDGET_RESIZED: void;
  WIDGET_METRICS_EDITED: void;
  WIDGET_DRILLDOWNS_EDITED: void;
  WIDGET_DATE_EDITED: { widgetType?: string };
  WIDGET_DUPLICATED: { widgetType: string; widgetId: string };
  WIDGET_FILTERS_EDITED: void;
  WIDGET_CHART_TYPE_EDITED: { widgetType: string; newType: string };
  WIDGET_CHART_COLOR_THEME_EDITED: { colorThemeType: ColorThemeType };
  WIDGET_CHART_WORKSPACE_COLOR_THEME_UPDATED: void;
  WIDGET_CHART_CUSTOM_COLOR_THEME_EDITED: void;
  WIDGET_CHART_ABSOLUTE_MODE_TOGGLED: { isEnabled: boolean };
  WIDGET_CHART_DISPLAY_LABELS_TOGGLED: { isEnabled: boolean };
  WIDGET_TABLE_ROW_REORDERED: { widgetType: string };
  WIDGET_EXPORTED: { widgetType: string; exportType: string };
  WIDGET_DELETED: { widgetType: string; chartType: string };
  WIDGET_SCENARIO_CHANGED: {
    scenario: { dimensionName: string; members: string[] }[];
  };
  WIDGET_SCENARIOS_CHANGED: { scenarios: UUID[] };
  WIDGET_FULLSCREEN_TOGGLED: { widgetType: string; isFullscreen: boolean };
  WIDGET_INSPECTION_OPENED: {
    widgetId: string;
    widgetType: string;
    chartType?: string;
  };
  WIDGET_DYNAMIC_SCENARIO_DATE_RANGE_USED: {
    widgetId: UUID;
    scenarioId: UUID;
    rangeType: ScenarioDateRangeType;
  };
  WIDGET_HAS_MULTIPLE_SCENARIO_DATE_RANGES: {
    widgetId: UUID;
    scenarioDateRangesCount: number;
  };
  WIDGET_BACK_TO_REPORT_BACK_BUTTON_CLICKED: void;
  WIDGET_METRICS_SEARCHED: void;
  WIDGET_NEW_DATABLOCK_LINK_CLICKED: void;
  WIDGET_NEW_CALCULATION_LINK_CLICKED: void;
  WIDGET_NEW_TIME_SERIES_LINK_CLICKED: void;
  WIDGET_TABLE_COLUMN_GROUP_CREATED: { groupsCount: number };
  WIDGET_TABLE_COLUMN_GROUP_DELETED: { groupsCount: number };
  WIDGET_TABLE_COLUMNS_VISIBILITY_CHANGED: {
    hiddenColumnsCount: number;
    visibleColumnsCount: number;
  };
  WIDGET_TABLE_DISPLAY_ACCOUNTS_TOGGLED: { isEnabled: boolean };
  WIDGET_TABLE_SUB_ROWS_CHANGED: { subRows: Nullable<string> };
  WIDGET_TABLE_COMPACT_ROWS_TOGGLED: { isEnabled: boolean };
  WIDGET_TABLE_FREEZE_FIRST_COLUMN_TOGGLED: { isEnabled: boolean };
  WIDGET_TABLE_FREEZE_HEADER_TOGGLED: { isEnabled: boolean };
  WIDGET_TABLE_CURRENCY_SYMBOL_CHANGED: {
    currencySymbol: string;
  };
  WIDGET_TABLE_NEGATIVE_NUMBER_FORMAT_CHANGED: {
    negativeNumberFormat: NegativeNumberFormat;
  };
  WIDGET_TABLE_THOUSAND_SEPARATOR_CHANGED: {
    widgetId: string;
    thousandSeparator: string;
  };
  WIDGET_TABLE_DECIMAL_SEPARATOR_CHANGED: {
    widgetId: string;
    decimalSeparator: string;
  };
  WIDGET_TABLE_PRECISION_CHANGED: { precision: number };

  WIDGET_PIVOT_TABLE_CURRENCY_SYMBOL_CHANGED: {
    currencySymbol: string;
  };
  WIDGET_PIVOT_TABLE_NEGATIVE_NUMBER_FORMAT_CHANGED: {
    negativeNumberFormat: NegativeNumberFormat;
  };
  WIDGET_PIVOT_TABLE_THOUSAND_SEPARATOR_CHANGED: {
    widgetId: string;
    thousandSeparator: string;
  };
  WIDGET_PIVOT_TABLE_DECIMAL_SEPARATOR_CHANGED: {
    widgetId: string;
    decimalSeparator: string;
  };
  WIDGET_PIVOT_TABLE_PRECISION_CHANGED: { precision: number };

  WIDGET_CHART_CURRENCY_SYMBOL_CHANGED: { currencySymbol: string };
  WIDGET_CHART_NEGATIVE_NUMBER_FORMAT_CHANGED: {
    negativeNumberFormat: NegativeNumberFormat;
  };
  WIDGET_CHART_THOUSAND_SEPARATOR_CHANGED: {
    thousandSeparator: string;
  };
  WIDGET_CHART_DECIMAL_SEPARATOR_CHANGED: {
    decimalSeparator: string;
  };
  WIDGET_WATERFALL_CHART_CURRENCY_SYMBOL_CHANGED: { currencySymbol: string };
  WIDGET_WATERFALL_CHART_NEGATIVE_NUMBER_FORMAT_CHANGED: {
    negativeNumberFormat: NegativeNumberFormat;
  };
  WIDGET_WATERFALL_CHART_THOUSAND_SEPARATOR_CHANGED: {
    thousandSeparator: string;
  };
  WIDGET_WATERFALL_CHART_DECIMAL_SEPARATOR_CHANGED: {
    decimalSeparator: string;
  };
  WIDGET_CHART_PRECISION_CHANGED: { widgetId: string; precision: number };
  WIDGET_COMBO_CHART_CURRENCY_SYMBOL_CHANGED: { currencySymbol: string };
  WIDGET_COMBO_CHART_NEGATIVE_NUMBER_FORMAT_CHANGED: {
    negativeNumberFormat: NegativeNumberFormat;
  };
  WIDGET_COMBO_CHART_THOUSAND_SEPARATOR_CHANGED: {
    thousandSeparator: string;
  };
  WIDGET_COMBO_CHART_DECIMAL_SEPARATOR_CHANGED: {
    decimalSeparator: string;
  };
  WIDGET_COMBO_CHART_PRECISION_CHANGED: { widgetId: string; precision: number };
  WIDGET_COMBO_CHART_SERIES_VISIBILITY_CHANGED: {
    hiddenSeriesCount: number;
    visibleSeriesCount: number;
  };
  WIDGET_COMBO_CHART_DRILLDOWN_CHANGED: {
    axis: string;
    category1: string;
    category2: string;
  };
  WIDGET_KPI_CURRENCY_SYMBOL_CHANGED: { currencySymbol: string };
  WIDGET_KPI_NEGATIVE_NUMBER_FORMAT_CHANGED: {
    negativeNumberFormat: NegativeNumberFormat;
  };
  WIDGET_KPI_THOUSAND_SEPARATOR_CHANGED: {
    thousandSeparator: string;
  };
  WIDGET_KPI_DECIMAL_SEPARATOR_CHANGED: {
    decimalSeparator: string;
  };
  WIDGET_KPI_PRECISION_CHANGED: { widgetId: string; precision: number };
  WIDGET_TABLE_STYLE_CHANGED: {
    tableRowStylesCount: number;
    tableRowStyles: string[][];
  };
  //#endregion

  //#region Widget Template Events
  WIDGET_TEMPLATE_CREATED: void;
  WIDGET_TEMPLATE_RENAMED: void;
  WIDGET_TEMPLATE_DELETED: void;
  WIDGET_TEMPLATE_OVERWRITTEN: void;
  //#endregion

  //#region Metrics Events
  METRICS_PAGINATION_CHANGED: {
    pageSize: number;
    currentPage: number;
    totalItems: number;
  };
  METRICS_SEARCH_CHANGED: void;
  //#endregion

  //#region Dimensions Events
  DIMENSION_TABLE_PAGINATION_CHANGED: {
    pageSize: number;
    currentPage: number;
    totalItems: number;
  };
  //#endregion

  //#region Datablock Events
  CREATE_DATABLOCKS_CLICKED: { creationSource: string };
  APPLY_CHANGES_CREATE_DATABLOCKS_CLICKED: { integrationId: UUID };
  DATABLOCK_CREATED: {
    creationSource: string;
    integrationId?: string;
    listName?: string;
    datablocksCount?: number;
  };
  DATABLOCK_EDITED: void;
  DATABLOCK_DELETED: { integrationId?: string; datablocksCount: number };
  DATABLOCK_OPENED: void;
  DATABLOCKS_SEARCH_CHANGED: void;
  DATABLOCKS_PAGINATION_CHANGED: void;
  DATABLOCK_FILTER_CLEARED: { datablockId?: string };
  DATABLOCK_LIST_CHANGED: {
    datablockId?: string;
    listName: string;
  };
  DATABLOCK_MEASURE_CHANGED: {
    datablockId?: string;
    measure: Nullable<string>;
  };
  DATABLOCK_PREVIEW_DATE_SELECTED: { dates: { year: number; month: number }[] };
  DATABLOCK_RANGE_SELECTOR_SUBMITTED: {
    columnName: string;
    range?: {
      from: number;
      to: number;
    };
  };
  DATABLOCK_RANGE_SELECTOR_DELETED: {
    columnName: string;
    range?: {
      from: number;
      to: number;
    };
  };
  DATABLOCK_RANGES_COUNT_CHANGED: {
    columnName: string;
    rangesCount: number;
  };
  //#endregion

  //#region Calculation Events
  CALCULATION_CREATED: { creationSource: string };
  CALCULATION_EDITED: {
    operatorsCount: number;
    dataBlocksCount: number;
    calculationsCount: number;
    timeSeriesCount: number;
  };
  CALCULATION_EDITED_WITH_TIME_SERIES: {
    operatorsCount: number;
    dataBlocksCount: number;
    calculationsCount: number;
    timeSeriesCount: number;
  };
  CALCULATION_DELETED: { calculationId: string };
  CALCULATION_OPENED: { calculationId: string };
  CALCULATION_CLEARED: { calculationId: string };
  CALCULATION_FORMAT_CHANGED: { calculationId: string; format: string };
  CALCULATION_OPERAND_ADDED: {
    source: 'add_button' | 'formula_composer';
    operandType: string;
  };
  CALCULATION_OPERAND_SELECTED: {
    operandType: string;
  };
  CALCULATION_OPERAND_ACTION: {
    action: 'more_clicked' | 'deleted';
    operandId?: string;
  };
  CALCULATION_TRANSFORMATION_ACTION: {
    action: 'added' | 'cleared_all' | 'type_selected' | 'deleted';
    transformationType?: string;
  };
  CALCULATION_TRANSFORMATION_CONFIGURED: {
    transformationType: string | undefined;
    column?: string;
    aggregationType?: string;
    groupBySelectAll?: boolean;
    groupByNone?: boolean;
  };
  CALCULATION_TRANSFORMATION_PANEL_TOGGLED: { isMinimized: boolean };
  CALCULATION_PREVIEW_DATE_CHANGED: { month: number; year: number };
  CALCULATION_FORMULA_VALIDATION_FAILED: void;
  //#endregion

  //#region Integration Events
  INTEGRATION_SYNC_TRIGGERED: {
    integrationId: string;
    fiscalYear?: MaybeNullable<number>;
  };
  INTEGRATION_API_CONNECTION_REQUESTED: { integrationType: string };
  INTEGRATION_ADDED: { integrationType: string };
  INTEGRATION_DELETED: { integrationId: string };
  INTEGRATION_ZIP_UPLOADED: { isBudget?: boolean };
  INTEGRATION_BACK_TO_INTEGRATIONS_BACK_BUTTON_CLICKED: void;
  //#endregion

  //#region Scenario Events
  SCENARIO_ADDED: { scenarioName: string };
  PREPARE_PREVIEW_CLICKED: { scenarioId: UUID };
  VIEW_PREVIEW_CLICKED: { scenarioId: UUID };
  SHARE_SCENARIO_CLICKED: { scenarioId: UUID };
  SHARE_PLAN_CLICKED: { scenarioId: UUID; planId: UUID };
  RESTRICT_PLANNING_SCOPE_CLICKED: { scenarioId: UUID; planId: UUID };
  SCENARIO_DELETED: { scenarioId: string };
  SCENARIO_BACK_TO_SCENARIOS_BACK_BUTTON_CLICKED: void;
  SCENARIO_RENAMED: { scenarioId: string; oldName: string; newName: string };
  SCENARIO_PLAN_IMPORTED: { scenarioId: UUID; planId: UUID; planName: string };
  SCENARIO_IMPORTED: { scenarioId: UUID; scenarioName: string };

  ALLOCATION_SCENARIO_ADDED: { allocationScenarioName: string };
  ALLOCATION_SCENARIO_RENAMED: {
    allocationScenarioId: string;
    oldName: string;
    newName: string;
  };
  ALLOCATION_SCENARIO_DELETED: { allocationScenarioId: string };
  ALLOCATION_SCENARIOS_SORTED: { sortBy: 'name' | 'createdAt' | 'updatedAt' };
  ALLOCATION_SCENARIO_TAB_CHANGED: { tab: 'allocations' | 'data' };
  ALLOCATION_SCENARIO_PUBLISHING_STARTED: { allocationScenarioId: string };
  ALLOCATION_SCENARIO_PUBLISHED: { allocationScenarioId: string };
  ALLOCATION_SCENARIO_SOURCE_SCENARIO_UPDATED: {
    allocationScenarioId: string;
    sourceScenarioId: string;
  };
  ALLOCATION_ADDED: { allocationName: string };
  ALLOCATION_RENAMED: {
    allocationId: string;
    oldName: string;
    newName: string;
  };
  ALLOCATION_DELETED: { allocationId: string };
  ALLOCATION_DUPLICATED: { allocationId: string; newAllocationName: string };
  ALLOCATION_BACK_TO_ALLOCATION_SCENARIOS_BACK_BUTTON_CLICKED: void;
  ALLOCATION_UPDATED: { allocationId: string; allocationName: string };
  ALLOCATION_DESTINATIONS_UPDATED: { allocationId: string };
  ALLOCATION_VIEW_DATE_CHANGED: { date: number; allocationId: Maybe<string> };
  ALLOCATION_DESTINATION_VALUE_CHANGED: {
    destinationId: string;
    value: number;
    allocationId: Maybe<string>;
  };
  ALLOCATION_DESTINATION_FORMULA_CHANGED: {
    destinationId: string;
    formula: string;
    allocationId: Maybe<string>;
  };
  //#endregion

  //#region Plan Events
  IMPORT_PLAN_ADDED: { planName: string; scenarioId: UUID };
  BUILD_PLAN_ADDED: { planName: string; scenarioId: UUID };
  PLAN_DELETED: { planId: UUID; planType: 'build' | 'import' };
  PLAN_RENAMED: { planId: UUID; oldName: string; newName: string };
  PLAN_VIEWED: { planId: UUID; planType: 'build' | 'import' };
  PLAN_UPDATED: {
    planId: UUID;
    changes: Array<
      | 'date_range'
      | 'distribution'
      | 'formula'
      | 'recipe'
      | 'targets'
      | 'target_values'
    >;
  };
  PLAN_DISTRIBUTION_TAB_CHANGED: { planId: UUID; tab: string };
  SCENARIO_PLAN_PUBLISHING_STARTED: { scenarioId: UUID };
  SCENARIO_PLAN_PUBLISHING_SUCCEEDED: { scenarioId: UUID };
  SCENARIO_PLAN_PUBLISHING_FAILED: { scenarioId: UUID };
  //#endregion

  //#region General Events
  LANGUAGE_SWITCHED: { selectedLanguage: string };
  HEADER_NAVIGATION_USED: {
    from: string;
    to: string;
  };
  BREADCRUMB_NAVIGATION_USED: { from: string; to: BreadcrumbItem };
  HEADER_UPGRADE_NOW_CLICKED: void;
  SIDEBAR_NAVIGATION_USED: { from: string; to: string };
  NAV_SIDEBAR_STATE_CHANGED: { state: 'expanded' | 'compact' };
  //#endregion

  //#region User management Events
  USER_CREATED: { usersCount: number; userCreationSource: string };
  USER_DELETED: { usersCount: number };
  //#endregion

  //#region Budget Events
  BUDGET_TEMPLATE_DOWNLOADED: void;
  BUDGET_UPLOADED: { budgetName: string };
  //#endregion

  //#region Help and Guidance Events
  HELP_GUIDANCE_PRODUCT_DOCS_CLICKED: void;
  HELP_GUIDANCE_CALCULATIONS_CLICKED: { source?: string };
  HELP_GUIDANCE_DATABLOCKS_CLICKED: void;
  HELP_GUIDANCE_DATABLOCK_PREVIEW_CLICKED: void;
  HELP_GUIDANCE_CALCULATION_PREVIEW_CLICKED: void;
  HELP_GUIDANCE_DATABLOCK_MEASURES_CLICKED: void;
  HELP_GUIDANCE_ACCOUNT_RANGES_CLICKED: void;
  HELP_GUIDANCE_SUPPORT_CLICKED: void;
  HELP_GUIDANCE_TIME_OFFSET_CLICKED: void;
  HELP_GUIDANCE_BUDGET_CLICKED: void;
  HELP_GUIDANCE_CHART_CLICKED: void;
  HELP_GUIDANCE_PERMISSIONS_CLICKED: void;
  HELP_GUIDANCE_FOLDERS_CLICKED: void;
  HELP_GUIDANCE_INTEGRATIONS_CLICKED: void;
  HELP_GUIDANCE_REPORT_CLICKED: void;
  HELP_GUIDANCE_TABLE_CLICKED: void;
  HELP_GUIDANCE_PIVOT_TABLE_CLICKED: void;
  HELP_GUIDANCE_DRILLDOWN_CLICKED: void;
  HELP_GUIDANCE_HOW_INTEGRATIONS_WORK: void;
  HELP_GUIDANCE_HOW_BUDGETS_WORK: void;
  HELP_GUIDANCE_TEMPLATES_CLICKED: void;
  HELP_GUIDANCE_FORMATTING_CLICKED: void;
  HELP_GUIDANCE_HOW_SCENARIOS_WORK: void;
  HELP_GUIDANCE_ACCESS_RIGHTS_CLICKED: void;
  HELP_GUIDANCE_TRANSFORMATIONS_CLICKED: { source?: string };
  HELP_GUIDANCE_HOW_ALLOCATION_SCENARIOS_WORK: void;
  HELP_GUIDANCE_HOW_ALLOCATION_FORMULAS_WORK: void;
  HELP_GUIDANCE_HOW_PLAN_TARGET_FORMULAS_WORK: void;
  HELP_GUIDANCE_HOW_TIME_SERIES_WORK: void;
  HELP_GUIDANCE_HOW_TAGS_WORK: void;
  HELP_GUIDANCE_HOW_PERSONIO_WORK: void;
  HELP_GUIDANCE_METRIC_UNAVAILABLE_CLICKED: void;
  //#endregion

  //#region Folder Events
  FOLDER_CREATED: { creationSource: string };
  FOLDER_RENAMED: { folderName: string; newFolderName: string };
  FOLDER_DELETED: { folderName: string };
  FOLDER_MOVED: { sourceFolderName: string; destinationFolderName: string };
  //#endregion

  //#region Drawer Events
  DRAWER_TOGGLED: { name: string; isOpen: boolean };
  //#endregion

  //#region Inspection Events
  INSPECTION_SEARCH_CHANGED: void;
  INSPECTION_CONFIGURATION_CHANGED: void;
  //#endregion

  //#region OPOS Events
  OPOS_CUT_OFF_DATE_CHANGED: { context: BookingsContext };
  OPOS_FILTER_APPLIED: {
    context: BookingsContext;
    values: Array<'yes' | 'no' | 'none'>;
  };
  DUE_DATE_FILTER_APPLIED: { context: BookingsContext };
  OP_NUMBER_FILTER_APPLIED: { context: BookingsContext };
  DOCUMENT_NUMBER_FILTER_APPLIED: { context: BookingsContext };
  INVOICE_PICTURE_LINK_CLICKED: { context: BookingsContext };
  OPEN_BALANCE_CUT_OFF_DATE_CHANGED: void;
  OPEN_BALANCE_FILTER_APPLIED: void;
  PAYMENT_BEHAVIOR_FILTER_APPLIED: void;
  //#endregion

  //#region Time Series Events
  TIME_SERIES_CREATED: void;
  TIME_SERIES_USED_IN_WIDGET: { timeSeriesId: string };
  TIME_SERIES_REMOVED_FROM_WIDGET: { timeSeriesId: string };
  //#endregion
};

const analyticsManager = createAnalyticsManager<UserEvents>();

const initializeAnalytics = async () => {
  const email = authManager.idTokenParsed?.email;
  const userId = authManager.idTokenParsed?.sub;
  const organizationId = authManager.idTokenParsed?.organization_id;
  const resolvedOrganizationId = Array.isArray(organizationId)
    ? organizationId[0]
    : organizationId;
  const isEmailInternal = Boolean(email && email.endsWith('@helu.io'));
  const shouldIgnoreUser = isPreviewEnvironment()
    ? true
    : isProductionEnvironment()
      ? !appEnv.ANALYTICS_ENABLED || isEmailInternal
      : !appEnv.ANALYTICS_ENABLED;

  await analyticsManager.initialize({
    mixpanelToken: appEnv.ANALYTICS_API_TOKEN,
    mixpanelApiHost: appEnv.ANALYTICS_API_HOST,
    debugMode: !isProductionEnvironment(),
    shouldIgnoreUser,
    userProperties: {
      userId: userId || 'anonymous',
    },
  });
  await analyticsManager.setGroup('organization_id', resolvedOrganizationId);
};

export { analyticsManager, initializeAnalytics };
